<template>
  <div v-if="!authStore.isAuthenticated" class="max-w-4xl mx-auto">
    <div class="card bg-base-100 card-bordered">
      <div class="card-body text-center">
        <h2 class="card-title text-2xl mb-4 justify-center">Authentication Required</h2>
        <p class="text-base-content/70 mb-6">
          You need to be logged in to create a competition.
        </p>
        <router-link to="/auth" class="btn btn-primary">
          Login or Register
        </router-link>
      </div>
    </div>
  </div>

  <div v-else class="max-w-4xl mx-auto">
    <div class="grid lg:grid-cols-3 gap-6">
      <!-- Main Form -->
      <div class="lg:col-span-2">
        <div class="card bg-base-100 card-bordered">
          <div class="card-body">
            <h2 class="card-title text-2xl mb-6">Create New Competition</h2>
            
            <form @submit.prevent="handleSubmit" class="space-y-6">
              <!-- Competition Name -->
              <div class="form-control">
                <label class="label">
                  <span class="label-text">Competition Name *</span>
                </label>
                <input 
                  v-model="form.name"
                  type="text" 
                  placeholder="Enter competition name"
                  class="input input-bordered" 
                  :class="{ 'input-error': showErrors && !form.name.trim() }"
                  required
                />
                <label v-if="showErrors && !form.name.trim()" class="label">
                  <span class="label-text-alt text-error">Competition name is required</span>
                </label>
              </div>

              <!-- Competition Type (Disabled for now) -->
              <div class="form-control">
                <label class="label">
                  <span class="label-text">Competition Type</span>
                </label>
                <select class="select select-bordered" disabled>
                  <option value="returns">Returns Competition</option>
                  <option value="short" disabled>Short Competition (Coming Soon)</option>
                  <option value="peak" disabled>Peak Performance (Coming Soon)</option>
                </select>
                <label class="label">
                  <span class="label-text-alt">
                    Winner is determined by highest total return at competition close
                  </span>
                </label>
              </div>

              <!-- Start Date and Duration on same row -->
              <div class="grid md:grid-cols-2 gap-4">
                <!-- Start Date (Monday Only) -->
                <div class="form-control">
                  <label class="label">
                    <span class="label-text">Start Date (Mondays Only) *</span>
                  </label>
                  <input 
                    v-model="form.startDate"
                    type="date" 
                    class="input input-bordered" 
                    :class="{ 'input-error': showErrors && !form.startDate }"
                    :min="nextMonday"
                    required
                    @change="validateMonday"
                  />
                  <label class="label">
                    <span class="label-text-alt">
                      Competitions start on Monday at market open (9:30 AM ET)
                    </span>
                  </label>
                  <label v-if="showErrors && !form.startDate" class="label">
                    <span class="label-text-alt text-error">Start date is required</span>
                  </label>
                </div>

                <!-- Duration in Weeks -->
                <div class="form-control">
                  <label class="label">
                    <span class="label-text">Duration (weeks) *</span>
                  </label>
                  <input 
                    v-model="form.durationWeeks"
                    type="number" 
                    min="1"
                    max="52"
                    placeholder="4"
                    class="input input-bordered" 
                    :class="{ 'input-error': showErrors && (!form.durationWeeks || form.durationWeeks < 1) }"
                    required
                  />
                  <label class="label">
                    <span class="label-text-alt">
                      Each week runs Monday-Friday. Competition ends on Friday at market close (4:00 PM ET)
                    </span>
                  </label>
                  <label v-if="showErrors && (!form.durationWeeks || form.durationWeeks < 1)" class="label">
                    <span class="label-text-alt text-error">Duration must be at least 1 week</span>
                  </label>
                </div>
              </div>

              <!-- Required Stock Count -->
              <div class="form-control">
                <label class="label">
                  <span class="label-text">Number of Stocks Required *</span>
                </label>
                <input 
                  v-model="form.requiredStockCount"
                  type="number" 
                  min="1"
                  max="4"
                  placeholder="1"
                  class="input input-bordered" 
                  :class="{ 'input-error': showErrors && (!form.requiredStockCount || form.requiredStockCount < 1) }"
                  required
                />
                <label class="label">
                  <span class="label-text-alt">
                    Choose between 1-4 stocks per participant (default: 1)
                  </span>
                </label>
                <label v-if="showErrors && (!form.requiredStockCount || form.requiredStockCount < 1)" class="label">
                  <span class="label-text-alt text-error">Number of stocks is required</span>
                </label>
              </div>

              <!-- Allow Weights -->
              <div class="form-control">
                <label class="label cursor-pointer">
                  <span class="label-text">Allow Stock Weights</span>
                  <input 
                    v-model="form.allowWeights"
                    type="checkbox" 
                    class="checkbox checkbox-primary" 
                  />
                </label>
                <label class="label">
                  <span class="label-text-alt">
                    If enabled, participants can assign weights to their stock picks (must sum to 100%)
                  </span>
                </label>
              </div>

              <!-- Competition Type -->
              <div class="form-control">
                <label class="label">
                  <span class="label-text">Competition Type</span>
                </label>
                <select 
                  v-model="form.competitionType"
                  class="select select-bordered"
                  required
                >
                  <option value="private">Private - Small groups, unique stocks</option>
                  <option value="public">Public - Large groups, no stock restrictions</option>
                </select>
                <label class="label">
                  <span class="label-text-alt">
                    <strong>Private:</strong> First-come-first-serve stock selection. 
                    <strong>Public:</strong> No stock restrictions, anyone can pick any stocks.
                  </span>
                </label>
              </div>

              <!-- Stock Uniqueness (only for private competitions) -->
              <div v-if="form.competitionType === 'private'" class="form-control">
                <label class="label cursor-pointer">
                  <span class="label-text">Enforce Stock Uniqueness</span>
                  <input 
                    v-model="form.stockUniquenessEnabled"
                    type="checkbox" 
                    class="checkbox checkbox-primary" 
                  />
                </label>
                <label class="label">
                  <span class="label-text-alt">
                    If enabled, each stock combination can only be picked once (first-come-first-serve)
                  </span>
                </label>
              </div>

              <!-- Show Picks Before Start -->
              <div class="form-control">
                <label class="label cursor-pointer">
                  <span class="label-text">Show Picks Before Competition Starts</span>
                  <input 
                    v-model="form.showPicksBeforeStart"
                    type="checkbox" 
                    class="checkbox checkbox-primary" 
                  />
                </label>
                <label class="label">
                  <span class="label-text-alt">
                    If enabled, participants can see each other's stock picks before the competition begins
                  </span>
                </label>
              </div>

              <!-- Max Participants (only for public competitions) -->
              <div v-if="form.competitionType === 'public'" class="form-control">
                <label class="label">
                  <span class="label-text">Maximum Participants</span>
                </label>
                <input 
                  v-model="form.maxParticipants"
                  type="number" 
                  min="1"
                  max="10000"
                  placeholder="1000"
                  class="input input-bordered" 
                />
                <label class="label">
                  <span class="label-text-alt">
                    Leave empty for unlimited participants
                  </span>
                </label>
              </div>

              <!-- Anonymize Entry Names -->
              <div class="form-control">
                <label class="label cursor-pointer">
                  <span class="label-text">Anonymize Entry Names</span>
                  <input 
                    v-model="form.anonymizeNames"
                    type="checkbox" 
                    class="checkbox checkbox-primary"
                  />
                </label>
                <label class="label">
                  <span class="label-text-alt">
                    If enabled, participants must use display names instead of their real names on the leaderboard
                  </span>
                </label>
              </div>

              <!-- Submit Button -->
              <div class="form-control mt-6">
                <button 
                  type="submit" 
                  class="btn btn-primary"
                  :disabled="loading || !isFormValid"
                >
                  <span v-if="loading" class="loading loading-spinner loading-sm"></span>
                  {{ loading ? 'Creating...' : 'Create Competition' }}
                </button>
              </div>
            </form>

            <!-- Error Display -->
            <div v-if="error" class="alert alert-error mt-4">
              <i class="mdi mdi-alert-circle h-6 w-6"></i>
              <span>{{ error }}</span>
            </div>

            <!-- Success Display -->
            <div v-if="createdCompetition" class="alert alert-success mt-4">
              <i class="mdi mdi-check-circle h-6 w-6"></i>
              <div>
                <h3 class="font-bold">Competition Created!</h3>
                <div class="text-xs">
                  Share this link with friends: 
                  <code class="bg-base-300 px-2 py-1 rounded">
                    {{ shareUrl }}
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Date Preview Sidebar -->
      <div class="lg:col-span-1">
        <div class="card bg-base-100 card-bordered sticky top-4">
          <div class="card-body">
            <h3 class="card-title text-lg mb-4">Competition Timeline</h3>
            
            <div v-if="form.startDate && form.durationWeeks" class="space-y-4">
              <div>
                <p class="text-sm text-base-content/70">Start Date</p>
                <p class="font-semibold">{{ formatDate(form.startDate) }}</p>
                <p class="text-xs text-base-content/70">Monday at 9:30 AM ET</p>
              </div>
              
              <div>
                <p class="text-sm text-base-content/70">End Date</p>
                <p class="font-semibold">{{ formatDate(endDate) }}</p>
                <p class="text-xs text-base-content/70">Friday at 4:00 PM ET</p>
              </div>
              
              <div>
                <p class="text-sm text-base-content/70">Duration</p>
                <p class="font-semibold">{{ form.durationWeeks }} week{{ form.durationWeeks > 1 ? 's' : '' }}</p>
                <p class="text-xs text-base-content/70">{{ totalDays }} trading days</p>
              </div>
            </div>
            
            <div v-else class="text-center py-8">
              <div class="text-4xl mb-4">📅</div>
              <p class="text-base-content/70 text-sm">
                Select a start date and duration to see the competition timeline
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useCompetitionStore } from '../stores/competition'
import { useAuthStore } from '../stores/auth'

const router = useRouter()
const competitionStore = useCompetitionStore()
const authStore = useAuthStore()

const form = reactive({
  name: '',
  startDate: '',
  durationWeeks: 4,
  requiredStockCount: 1,
  allowWeights: true,
  anonymizeNames: false,
  competitionType: 'private',
  stockUniquenessEnabled: false,
  showPicksBeforeStart: false,
  maxParticipants: 1000
})

const loading = ref(false)
const error = ref('')
const createdCompetition = ref(null)
const showErrors = ref(false)

// Get next Monday as minimum date
const nextMonday = computed(() => {
  const today = new Date()
  const daysUntilMonday = (8 - today.getDay()) % 7
  const nextMonday = new Date(today)
  nextMonday.setDate(today.getDate() + daysUntilMonday)
  return nextMonday.toISOString().split('T')[0]
})

// Calculate end date
const endDate = computed(() => {
  if (!form.startDate || !form.durationWeeks) return null
  
  const start = new Date(form.startDate)
  const end = new Date(start)
  end.setDate(start.getDate() + (form.durationWeeks * 7) - 3) // Subtract 3 days to end on Friday
  return end.toISOString().split('T')[0]
})

// Calculate total trading days
const totalDays = computed(() => {
  if (!form.durationWeeks) return 0
  return form.durationWeeks * 5 // 5 trading days per week
})

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })
}

// Validate that selected date is a Monday
const validateMonday = () => {
  if (form.startDate) {
    const selectedDate = new Date(form.startDate)
    if (selectedDate.getDay() !== 1) { // 1 = Monday
      error.value = 'Competition must start on a Monday'
      form.startDate = ''
    } else {
      error.value = ''
    }
  }
}

// Computed property to safely access window.location
const shareUrl = computed(() => {
  if (createdCompetition.value && typeof window !== 'undefined') {
    return `${window.location.origin}/join?id=${createdCompetition.value.id}`
  }
  return ''
})

const handleSubmit = async () => {
  // Show validation errors
  showErrors.value = true
  
  // Check if form is valid
  if (!isFormValid.value) {
    return
  }
  
  loading.value = true
  error.value = ''
  createdCompetition.value = null

  try {
    // Validate Monday start date
    if (form.startDate) {
      const selectedDate = new Date(form.startDate)
      if (selectedDate.getDay() !== 1) {
        throw new Error('Competition must start on a Monday')
      }
    }

    const competitionData = {
      name: form.name,
      start_date: new Date(form.startDate).toISOString(),
      duration_days: parseInt(form.durationWeeks) * 5, // Convert weeks to trading days
      required_stock_count: parseInt(form.requiredStockCount),
      allow_weights: form.allowWeights,
      anonymize_names: form.anonymizeNames,
      is_public: form.competitionType === 'public',
      stock_uniqueness_enabled: form.stockUniquenessEnabled,
      show_picks_before_start: form.showPicksBeforeStart,
      max_participants: form.maxParticipants ? parseInt(form.maxParticipants) : null
    }

    const result = await competitionStore.createCompetition(competitionData)
    createdCompetition.value = result
    
    // Reset form
    Object.assign(form, {
      name: '',
      startDate: '',
      durationWeeks: 4,
      requiredStockCount: 1,
      allowWeights: true,
      anonymizeNames: false,
      competitionType: 'private',
      stockUniquenessEnabled: false,
      showPicksBeforeStart: false,
      maxParticipants: 1000
    })
    showErrors.value = false
  } catch (err) {
    error.value = err.message || 'Failed to create competition'
  } finally {
    loading.value = false
  }
}

// Computed property to check if the form is valid
const isFormValid = computed(() => {
  return form.name.trim() && form.startDate && form.durationWeeks && form.requiredStockCount
})
</script> 